/**
 * @fileoverview 公共 Action 枚举与 EventMap（双端共享）
 *
 * 入选原则：**仅收录 web/accounts 两端均上报的 action**。
 * 两端字段完全一致的，直接定义；两端都上报但字段有差异的（如 XML_HTTP_REQUEST_ERROR，
 * web 多 duration/code），统一在公共层定义，差异字段标为可选，各端按需传。
 * 仅一端有的（如 jupyter/绘图系列仅 web），归各 app 自行扩展，不进 Common 层。
 *
 * 性能监控（perf）的 action 虽由 perf 插件内部上报，但所有接入方都会用到，
 * 故 PerfInfoEventMap/PerfErrorEventMap 一并合并到公共 EventMap，
 * AliWebLogger 的泛型默认值即包含 perf action，无需额外声明。
 *
 * 接入方扩展方式（纯类型层，无 as 断言）：
 * ```ts
 * // app 侧
 * import { CommonErrorAction, type CommonErrorEventMap } from '@peerup/browser-logger'
 *
 * export enum WebErrorAction { EDITOR_WS_ERROR = 'EDITOR_WS_ERROR' }
 * export interface WebErrorEventMap {
 *   [WebErrorAction.EDITOR_WS_ERROR]: { error: unknown; docId: string }
 * }
 *
 * // 合并：公共 + 扩展（交叉类型 + 对象展开）
 * export type ErrorEventMap = CommonErrorEventMap & WebErrorEventMap
 * export const ErrorAction = { ...CommonErrorAction, ...WebErrorAction }
 * ```
 */
import type { PerfErrorEventMap, PerfInfoEventMap } from '../plugins/perf/types'

// =====================================================================
// Error
// =====================================================================

/**
 * 公共错误 Action（双端共享）
 * @description 各枚举值与 web/accounts 两端对齐；字段两端不一致的归 app 扩展。
 * XML_HTTP_REQUEST_ERROR 例外：两端均上报，但可选字段（duration/traceId/code 等）
 * 各端按需传，故统一在公共层定义，可选字段标 ?。
 */
export enum CommonErrorAction {
  /** 路由错误 */
  ROUTER_ERROR = 'RouterError',
  /** XMLHttpRequest 请求失败（双端均为 {error}） */
  XML_HTTP_REQUEST_FAILED = 'XMLHttpRequestFailed',
  /** XMLHttpRequest 请求错误 */
  XML_HTTP_REQUEST_ERROR = 'XMLHttpRequestError',
}

/** 公共错误 EventMap（与 CommonErrorAction 一一对应 + perf 错误） */
export interface CommonErrorEventMap extends PerfErrorEventMap {
  [CommonErrorAction.ROUTER_ERROR]: {
    error: unknown
    to: unknown
    from: unknown
  }
  [CommonErrorAction.XML_HTTP_REQUEST_FAILED]: {
    error: unknown
  }
  [CommonErrorAction.XML_HTTP_REQUEST_ERROR]: {
    error: unknown
    duration?: number
    url?: string
    params?: unknown
    traceId?: string
    code?: number
  }
}

// =====================================================================
// Warn
// =====================================================================

/**
 * 公共警告 Action（双端 data 形状一致）
 * @description UPDATE_SCHEMA_VERSION 两端字段不一致（web 多 yjsSV）→ 归 app；
 * REQUEST_SLOW 仅 web 有 → 归 app
 */
export enum CommonWarnAction {
  /** 页面 DOM 元素过多 */
  PAGE_DOM_ELEMENT_TOO_MANY = 'PAGE_DOM_ELEMENT_TOO_MANY',
}

/** 公共警告 EventMap */
export interface CommonWarnEventMap {
  [CommonWarnAction.PAGE_DOM_ELEMENT_TOO_MANY]: {
    count: number
  }
}

// =====================================================================
// Info
// =====================================================================

/**
 * 公共信息 Action（双端 data 形状一致）
 * PERFORMANCE_METRIC/RESOURCE_MEASURE 等性能指标归 perf 插件专属枚举
 */
export enum CommonInfoAction {
  /** 页面正常卸载 */
  PAGE_UNLOAD = 'PAGE_UNLOAD',
  /** 页面出现卡顿 */
  PAGE_STUCK = 'PAGE_STUCK',
  /** 页面运行卡顿，30s 内卡顿 150ms 超过 5 次 */
  PAGE_RUN_STUCK = 'PAGE_RUN_STUCK',
  /** 页面单次卡顿超过 1s */
  PAGE_LONG_STUCK = 'PAGE_LONG_STUCK',
  /** 页面离开超过 12 小时，强制刷新 */
  PAGE_LEAVE_12H = 'PAGE_LEAVE_12H',
  /** 切换页面 */
  PAGE_TAB_CHANGE = 'PAGE_TAB_CHANGE',
  /** JS 内存使用超过 1024M */
  JS_MEMORY_USED_1024M = 'JS_MEMORY_USED_1024M',
  /** JS 内存使用率超过限制 */
  JS_MEMORY_USED_RATE_OVER_LIMIT = 'JS_MEMORY_USED_RATE_OVER_LIMIT',
  /** 异步组件加载失败，弹出错误提示框 */
  ASYNC_COMPONENT_REFRESH = 'ASYNC_COMPONENT_REFRESH',
}

/** 公共信息 EventMap（业务通用项 + perf 性能项） */
export interface CommonInfoEventMap extends PerfInfoEventMap {
  [CommonInfoAction.PAGE_UNLOAD]: {
    usedJSHeapSize?: number
    jsHeapSizeLimit?: number
  }
  [CommonInfoAction.PAGE_STUCK]: {
    duration?: number
    msg?: string
  }
  [CommonInfoAction.PAGE_RUN_STUCK]: {
    duration?: number
    msg?: string
  }
  [CommonInfoAction.PAGE_LONG_STUCK]: {
    duration?: number
    msg?: string
  }
  [CommonInfoAction.PAGE_TAB_CHANGE]: {
    key: string
    tabsList: unknown[]
  }
  [CommonInfoAction.PAGE_LEAVE_12H]: unknown
  [CommonInfoAction.JS_MEMORY_USED_1024M]: {
    used: number
    limit: number
  }
  [CommonInfoAction.JS_MEMORY_USED_RATE_OVER_LIMIT]: {
    used: number
    ratio: number
  }
  [CommonInfoAction.ASYNC_COMPONENT_REFRESH]: {
    url: string
  }
}
